import { useState, useEffect } from 'react';
import { Sheet } from '@hump/ui';
import { useAnnotationStore } from '../../stores/annotation';

export function AnnotationEditor() {
  const {
    editingBlockId,
    existingAnnotations,
    error,
    initialText,
    closeEditor,
    saveAnnotation,
    updateAnnotation,
    removeAnnotation,
  } = useAnnotationStore();
  const [text, setText] = useState('');
  // 8.6 编辑中批注 id（null = 新建模式）
  const [editingId, setEditingId] = useState<string | null>(null);

  // 8.2 划词批注：打开编辑器时用选中文本预填（作为初始内容/关联上下文）
  useEffect(() => {
    if (editingBlockId && !editingId) {
      setText(initialText);
    }
    // 仅在编辑器打开/初始文本变化时同步一次
  }, [editingBlockId, initialText]);

  async function handleSave() {
    if (editingId) {
      // 编辑已有批注
      await updateAnnotation(editingId, text);
    } else {
      // 新建批注
      await saveAnnotation(text);
    }
    setText('');
    setEditingId(null);
  }

  function handleClose() {
    closeEditor();
    setText('');
    setEditingId(null);
  }

  // 8.6 进入编辑某条已有批注
  function startEdit(id: string, currentText: string) {
    setEditingId(id);
    setText(currentText);
  }

  function cancelEdit() {
    setEditingId(null);
    setText('');
  }

  // 8.6 删除某条已有批注
  async function handleDelete(id: string) {
    await removeAnnotation(id);
  }

  return (
    <Sheet open={!!editingBlockId} onClose={handleClose} desktopVisible>
      <div className="p-4 space-y-3">
        <h3 className="text-base font-medium text-charcoal dark:text-charcoal-light">
          {editingId ? '编辑批注' : '添加批注'}
        </h3>

        {/* 8.6 已有批注列表 */}
        {existingAnnotations.length > 0 && !editingId && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-charcoal/60 dark:text-charcoal-light/60">
              已有批注（{existingAnnotations.length}）
            </p>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {existingAnnotations.map((a) => (
                <li
                  key={a.id}
                  className="rounded-md border border-border-warm dark:border-border-warm-dark px-2.5 py-1.5"
                >
                  <p className="text-sm text-charcoal dark:text-charcoal-light whitespace-pre-wrap break-words">
                    {a.text}
                  </p>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-charcoal/40 dark:text-charcoal-light/40">
                      {new Date(a.createdAt).toLocaleString('zh-CN')}
                    </span>
                    <span className="flex gap-1">
                      <button
                        onClick={() => startEdit(a.id, a.text)}
                        className="text-xs text-camel hover:underline px-1"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => handleDelete(a.id)}
                        className="text-xs text-error hover:underline px-1"
                      >
                        删除
                      </button>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={editingId ? '修改批注内容...' : '在这里写下你的笔记...'}
          rows={4}
          className="w-full rounded-md border border-border-warm dark:border-border-warm-dark bg-white dark:bg-warm-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-oasis resize-none"
        />
        <div className="flex gap-2 justify-end">
          {editingId && (
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 rounded-md text-sm text-charcoal/60 dark:text-charcoal-light/60 hover:bg-warm-white dark:hover:bg-border-warm-dark"
            >
              取消编辑
            </button>
          )}
          <button
            onClick={handleClose}
            className="px-3 py-1.5 rounded-md text-sm text-charcoal/60 dark:text-charcoal-light/60 hover:bg-warm-white dark:hover:bg-border-warm-dark"
          >
            关闭
          </button>
          <button
            onClick={handleSave}
            disabled={!text.trim()}
            className="px-3 py-1.5 rounded-md text-sm bg-camel text-white hover:bg-camel-light disabled:opacity-50"
          >
            保存
          </button>
        </div>
        {error && <p className="text-xs text-error">{error}</p>}
      </div>
    </Sheet>
  );
}
